import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Dialog from "@/components/ui/Dialog";
import { PLAN_LIMIT_EVENT, type PlanLimitCode } from "@/lib/planLimit";
import { paths } from "@/lib/addresses";
import { planQueryKey, usePlan } from "@/hooks/usePlan";
import { testIds } from "@/testIds";

/**
 * 4b · At the limit (launch redesign, Olivia 14 Oct): shown only when starting a document or an
 * import would go over the plan, whichever screen asked. Nothing was created. There's no Upgrade
 * button until checkout exists ("if it isn't built, it isn't shown").
 */
export function PlanLimitDialog() {
  const { t, i18n } = useTranslation("documents");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [code, setCode] = useState<PlanLimitCode | null>(null);
  const { data: plan } = usePlan(code !== null);

  useEffect(() => {
    const onLimit = (e: Event) => {
      setCode((e as CustomEvent<{ code: PlanLimitCode }>).detail.code);
      void queryClient.invalidateQueries({ queryKey: planQueryKey });
    };
    window.addEventListener(PLAN_LIMIT_EVENT, onLimit);
    return () => window.removeEventListener(PLAN_LIMIT_EVENT, onLimit);
  }, [queryClient]);

  if (!code) return null;
  const close = () => setCode(null);
  const documents = code === "PLAN_DOCUMENT_LIMIT";
  const name = plan?.plan.name ?? "Free";
  const resets = plan?.imports.resetsAt
    ? new Date(plan.imports.resetsAt).toLocaleDateString(i18n.language, { day: "numeric", month: "long" })
    : "";

  return (
    <Dialog isOpen onClose={close} onRequestClose={close} width={460}>
      <div data-testid={testIds.plan.limitDialog} data-limit={documents ? "documents" : "imports"} className="flex flex-col gap-3 pt-1">
        <h2 className="text-[17px] font-semibold text-[var(--lilia-text)]">
          {documents ? t("plan.limit.documentsTitle") : t("plan.limit.importsTitle")}
        </h2>
        <p className="text-[14px] leading-[1.55] text-[var(--lilia-text-secondary)]">
          {documents
            ? t("plan.limit.documentsBody", { plan: name, count: plan?.documents.limit ?? 3 })
            : t("plan.limit.importsBody", { plan: name, count: plan?.imports.limit ?? 3, date: resets })}
        </p>
        <div className="mt-2 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            data-testid={testIds.plan.limitDismiss}
            onClick={close}
            className="h-9 rounded-[9px] border border-[var(--lilia-border-strong)] px-4 text-[13px] font-medium text-[var(--lilia-text)] hover:bg-[var(--lilia-hover)]"
          >
            {documents ? t("plan.limit.notNow") : t("plan.limit.ok")}
          </button>
          {documents && (
            <button
              type="button"
              data-testid={testIds.plan.limitOpenDocuments}
              onClick={() => { close(); navigate(paths.documents); }}
              className="h-9 rounded-[9px] bg-[var(--lilia-primary)] px-4 text-[13px] font-semibold text-[color:var(--lilia-on-primary)]"
            >
              {t("plan.limit.openDocuments")}
            </button>
          )}
        </div>
      </div>
    </Dialog>
  );
}
